import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { CountryCode } from '@/store/ui';

import { RecentAndFavoriteOrdersToggleMode } from '../CmgRecentAndFavoriteOrdersToggle/cmg-recent-and-favorite-orders-toggle';
import { RecentOrFavoriteOrder, CustomerProfile } from '@/models';
import { Routes } from '@/router';
import { isDefined } from '@/helpers';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { AccountManagementEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgRecentAndFavoriteOrders',
})
export default class CmgRecentAndFavoriteOrders extends Vue {
  watchers: (() => void)[] = [];

  loadingFavorites = false;
  loadingRecents = false;

  mode: RecentAndFavoriteOrdersToggleMode = RecentAndFavoriteOrdersToggleMode.Favorites;

  @Prop({ type: Boolean })
  showRecents?: boolean;

  get favorites(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS];
  }

  get recents(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get orders(): RecentOrFavoriteOrder[] {
    return this.showingFavorites ? this.favorites : this.recents;
  }

  get showingFavorites(): boolean {
    return this.mode == RecentAndFavoriteOrdersToggleMode.Favorites;
  }

  get emptyHeartImageUrl(): string {
    return this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get favoriteText(): string {
    return this.countryCode === CountryCode.GB ? 'favourite' : 'favorite';
  }

  get favoritesText(): string {
    return this.countryCode === CountryCode.GB ? 'favourites' : 'favorites';
  }

  async mounted() {
    this.watchers.push(
      this.$appStore.watch(
        s => s.restaurant.selectedRestaurant,
        () => this.refreshFavorites(),
      ),
    );

    let setMode = false;

    // Respect the showRecents flag if provided
    if (isDefined(this.showRecents)) {
      this.mode = this.showRecents ? RecentAndFavoriteOrdersToggleMode.Recents : RecentAndFavoriteOrdersToggleMode.Favorites;
      setMode = true;
    }

    await this.refreshFavorites();

    // If we haven't resolved the mode yet, resolve it here
    if (!setMode) {
      // Default to Favorites on page load
      this.mode = RecentAndFavoriteOrdersToggleMode.Favorites;
    }
  }

  destroyed() {
    for (const watcher of this.watchers) {
      watcher();
    }
  }

  async refreshFavorites() {
    this.$services.logger.debug('Refreshing favorites');

    this.loadingFavorites = true;
    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    } finally {
      this.loadingFavorites = false;
    }
  }

  async refreshRecents() {
    this.$services.logger.debug('Refreshing recents');

    this.loadingRecents = true;
    try {
      await this.$services.recentAndFavoriteOrders.refreshRecentOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing recent orders', undefined, e);
    } finally {
      this.loadingRecents = false;
    }
  }

  async onToggle(mode: RecentAndFavoriteOrdersToggleMode) {
    this.mode = mode;
    const refreshMethod = mode === RecentAndFavoriteOrdersToggleMode.Recents ? this.refreshRecents : this.refreshFavorites;

    await refreshMethod();
  }

  isFavorite(order: RecentOrFavoriteOrder): boolean {
    return !!this.favorites.find(o => o.orderId == order.orderId);
  }

  isEligibleForReorder(order: RecentOrFavoriteOrder): boolean {
    return this.$services.recentAndFavoriteOrders.isEligibleForReorder(order, this.isEnrolledInLoyalty);
  }

  async onClickFavoriteToggleForOrder(order: RecentOrFavoriteOrder) {
    this.$services.ui.setLastSelectedElement('icon ' + order.orderId);
    const isFavorite = this.isFavorite(order);

    if (isFavorite) {
      this.$services.recentAndFavoriteOrders.removeOrderFromFavorites(order.orderId);
      return;
    }

    this.$services.ui.setModalVisibility('addFavoriteOrder', true, { order });
    this.$services.analytics.send(new AccountManagementEvent('orderFavorited', order.order.meals));
    this.$services.ui.firstFocus();
  }

  onClickOrderNow() {
    this.$router.push({ name: Routes.Home });
  }
}
